import { Box, Button, CardProps, Center, Flex, Spinner, Text } from '@chakra-ui/react'
import { Match } from 'effect'
import { usePropertyDetailsOwners } from 'features/PropertyDetails/infra/react/hooks/usePropertyDetailsOwners'
import { usePropertyDetailsStore } from 'features/PropertyDetails/infra/react/usePropertyDetailsState'
import { useSkiptraceStore } from 'features/Skiptrace/infra/react/Skiptrace.state'
import { Breakpoint } from 'presentation/components/Breakpoint'
import { SkiptraceFailedIcon, SkiptraceSuccessIcon } from 'presentation/components/Icons'
import { Pair, PairKey, PairValue } from 'presentation/components/Pair/Pair'
import { Tooltip } from 'presentation/components/Tooltip'
import { Card, CardPrimaryTitle } from 'presentation/components/molecules/Card'
import { useSwitchBreakpointFn } from 'presentation/hooks/useSwitchBreakpoint'
import { useLeadId } from 'presentation/libs/LeadIdContext'
import { mbp } from 'presentation/utils/mapBreakpoint'
import { isEmpty, pipe } from 'remeda'
import { isNonNullable } from 'utils/isNonNullable'

export type OwnerSkiptraceCardProps = {
  onSkiptraceAgain: () => void
} & CardProps

export const OwnerSkiptraceCard = (props: OwnerSkiptraceCardProps) => {
  const { onSkiptraceAgain, ...cardProps } = props
  const store = useOwnerSkiptraceCardData()

  const { sbp } = useSwitchBreakpointFn()

  if (!store) return null

  return (
    <Card
      size={mbp({ mobSm: 'xxs-locked', tabSm: 'sm-locked' })}
      bgColor='highlight.100'
      {...cardProps}
    >
      {pipe(
        Match.value(store),
        Match.when({ status: 'success' }, result => (
          <>
            <Flex justifyContent='space-between'>
              <CardPrimaryTitle>
                Owners
              </CardPrimaryTitle>
              <Button
                colorScheme={sbp({ mobSm: 'positive', tabSm: 'neutral' })}
                variant='outline'
                size='md'
                onClick={onSkiptraceAgain}
              >
                <Breakpoint
                  mobSm='Skip Again'
                  tabSm='Skiptrace Again'
                />
              </Button>
            </Flex>
            <Flex
              flexDirection={mbp({ mobSm: 'row', tabSm: 'column' })}
              mt={2}
              justifyContent='space-between'
              gap={3}
            >
              <Breakpoint
                mobSm={(
                  <Pair size='sm' minW={0} flex='1 1 50%'>
                    <PairKey>
                      Name
                    </PairKey>
                    <PairValue as='div' display='flex' flexDirection='column' gap={1}>
                      {result.owners.map((owner, index) => (
                        <Tooltip label={owner.name.full} key={index}>
                          <Text color='inherit' isTruncated>
                            {owner.name.full}
                          </Text>
                        </Tooltip>
                      ))}
                    </PairValue>
                  </Pair>
                )}
                tabSm={(
                  <Flex flexDirection='column' gap={1}>
                    {result.owners.map((owner, index) => (
                      <Flex
                        key={index}
                        alignItems='center'
                        gap={1}
                        textStyle='bodyL'
                        minW={0}
                      >
                        <SkiptraceSuccessIcon boxSize={4} color='positive.500' />
                        <Text color='inherit' isTruncated>
                          {owner.name.full}
                        </Text>
                        {/** @NOTE no age in propery details owners */}
                        {/* <Text color='inherit' ml='auto'>{owner.age}</Text> */}
                      </Flex>
                    ))}
                  </Flex>
                )}
              />

              <Pair size={mbp({ mobSm: 'sm', tabSm: 'md' })} minW={0} flex='1 1 50%'>
                <PairKey>
                  Owner&rsquo;s Address
                </PairKey>
                <PairValue as='div'>
                  <Breakpoint
                    mobSm={(
                      <Tooltip label={result.address}>
                        <Box noOfLines={2}>
                          {result.address}
                        </Box>
                      </Tooltip>
                    )}
                    tabSm={result.address}
                  />
                </PairValue>
              </Pair>
            </Flex>
          </>
        )),
        Match.when({ status: 'error' }, result => (
          <>
            <Flex justifyContent='space-between'>
              <CardPrimaryTitle>
                Owners
              </CardPrimaryTitle>
              <Button
                colorScheme={sbp({ mobSm: 'positive', tabSm: 'neutral' })}
                variant='outline'
                size='md'
                disabled
                opacity={0.5}
              >
                <Breakpoint
                  mobSm='Skip Again'
                  tabSm='Skiptrace Again'
                />
              </Button>
            </Flex>
            <Flex
              flexDirection={mbp({ mobSm: 'row', tabSm: 'column' })}
              mt={2}
              justifyContent='space-between'
              gap={3}
            >
              <Breakpoint
                mobSm={(
                  <Pair size='sm' minW={0} flex='1 1 50%'>
                    <PairKey>
                      Name
                    </PairKey>
                    <PairValue as='div' display='flex' flexDirection='column' gap={1}>
                      {result.owners.length > 0
                        ? result.owners.map((owner, index) => (
                          <Tooltip label={owner.name.full} key={index}>
                            <Text color='inherit' isTruncated>
                              {owner.name.full}
                            </Text>
                          </Tooltip>
                        ))
                        : (
                          <Text color='graystrong.300' fontStyle='italic'>
                            No owner data available
                          </Text>
                        )}
                    </PairValue>
                  </Pair>
                )}
                tabSm={(
                  <Flex flexDirection='column' gap={1}>
                    {result.owners.length > 0
                      ? result.owners.map((owner, index) => (
                        <Flex
                          key={index}
                          alignItems='center'
                          gap={1}
                          textStyle='bodyL'
                          minW={0}
                        >
                          <SkiptraceFailedIcon boxSize={4} color='negative.500' />
                          <Text color='inherit' isTruncated>
                            {owner.name.full}
                          </Text>
                        </Flex>
                      ))
                      : (
                        <Text color='graystrong.300' fontStyle='italic'>
                          No owner data available
                        </Text>
                      )}
                  </Flex>
                )}
              />

              <Pair size={mbp({ mobSm: 'sm', tabSm: 'md' })} minW={0} flex='1 1 50%'>
                <PairKey>
                  Owner&rsquo;s Address
                </PairKey>
                <PairValue as='div'>
                  <Text color='graystrong.300'>
                    {result.address !== '--' ? result.address : 'Address unavailable'}
                  </Text>
                </PairValue>
              </Pair>
            </Flex>
            <Text textStyle='bodyS' color='graystrong.300' mt={2} fontStyle='italic'>
              Skiptrace information unavailable due to error
            </Text>
          </>
        )),
        Match.orElse(() => (
          <Center h='full'>
            <Spinner />
          </Center>
        )),
      )}
    </Card>
  )
}

const useOwnerSkiptraceCardData = () => {
  const owners = usePropertyDetailsOwners()

  const skiptraceResult = useSkiptraceStore(store => {
    const resultState = store.entities.resultState

    // Handle error state - return error status instead of null
    if (resultState.status === 'error') {
      return {
        status: 'error' as const,
        address: '--', // Will be replaced with property details address below
        skiptraceAgain: () => {}, // Disabled function for error state
      }
    }

    if (resultState.status !== 'success')
      return null

    const result = resultState.result

    if (!result)
      return null

    const getAddress = () => {
      const address = result.subject.address

      const joinedAddressParts = [
        address?.line1 ? `${address.line1},` : null,
        address?.city ? `${address.city},` : null,
        address?.state,
        address?.postalCode,
      ].filter(isNonNullable).join(' ')

      return isEmpty(joinedAddressParts) ? '--' : joinedAddressParts
    }

    return {
      status: resultState.status,
      address: getAddress(),
      skiptraceAgain: () => {
        if (resultState.status === 'success') {
          void store.actions.performSkiptrace.execute({
            leadId: resultState.params.leadId,
            address: result.subject.address,
          })
        }
      },
    }
  })

  // Get owner address from property details as fallback
  const ownerAddress = usePropertyDetailsStore(propertyDetailsApi => {
    const routeLeadId = useLeadId()
    const propertyState = propertyDetailsApi.actions.getProperty.state
    const doesPropertyMatch = propertyState.status === 'success'
      && propertyState.data.leadId === routeLeadId

    if (!doesPropertyMatch) return '--'

    const ownership = propertyState.data.status === 'with-details'
      ? propertyState.data.ownership
      : null

    const address = ownership?.address

    if (!address) return '--'

    const joinedAddressParts = [
      address?.line1 ? `${address.line1},` : null,
      address?.city ? `${address.city},` : null,
      address?.state,
      address?.postalCode,
    ].filter(isNonNullable).join(' ')

    return isEmpty(joinedAddressParts) ? '--' : joinedAddressParts
  })

  // If skiptraceResult is null (due to loading or not ready), return null for the entire component
  if (!skiptraceResult)
    return null

  // For error state, use property details address as fallback
  const finalAddress = skiptraceResult.status === 'error' ? ownerAddress : skiptraceResult.address

  return {
    ...skiptraceResult,
    address: finalAddress,
    owners,
  }
}
